import { useQuery } from '@tanstack/react-query';
import {
  getSmerItem,
  ResponseGetSmerItem,
  Comparison,
} from '../api/getSmerItem';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { decrypt } from '@/utils/decrypt';
import { SkeletonSmerInner } from './SkeletonSmerInner';
import { motion } from 'framer-motion';
import { HeaderInfo } from '@/UI/HeaderInfo';
import i18n from '@/localisation/i18n';
import dayjs from 'dayjs';
import { InfoCard } from './components/InfoCard';
import { EmotionLabel } from '../components/EmotionLabel';
import { ErrorItem } from './components/ErrorItem';
import { Arguments } from './components/Arguments';
import { FadeReplace } from '@/UI/animations/FadeReplace';
import { useEffect, useState } from 'react';

export const SmerInner = () => {
  const { client, smer } = useParams();

  const { data, isFetching } = useQuery<ResponseGetSmerItem>({
    queryKey: ['smer', client, smer],
    staleTime: 5 * 60 * 1000,
    queryFn: () => getSmerItem({ user: client!, smerId: smer! }),
  });

  const [decryptedData, setDecryptedData] = useState<{
    event?: string;
    thoughts?: string;
    behaviour?: string;
    feelInBody?: string;
    solution?: string;
    conclusion?: string;
    comparison?: Comparison;
  }>({});

  useEffect(() => {
    const decryptAll = async () => {
      if (!data) return;

      const uid = client!;
      const results: any = {};

      const fieldsToDecrypt = [
        { key: 'event', bin: data.eventBin, plain: data.event },
        { key: 'thoughts', bin: data.thoughtsBin, plain: data.thoughts },
        { key: 'behaviour', bin: data.behaviourBin, plain: data.behaviour },
        { key: 'feelInBody', bin: data.feelInBodyBin, plain: data.feelInBody },
        { key: 'solution', bin: data.solutionBin, plain: data.solution },
        { key: 'conclusion', bin: data.conclusionBin, plain: data.conclusion },
      ];

      for (const field of fieldsToDecrypt) {
        if (field.bin) {
          try {
            results[field.key] = await decrypt(field.bin, uid);
          } catch (error) {
            Sentry.captureException(error);
            results[field.key] = field.plain;
          }
        } else {
          results[field.key] = field.plain;
        }
      }

      // Handle comparison special case
      if (data.comparisonBin) {
        try {
          const decrypted = await decrypt(data.comparisonBin, uid);
          results.comparison = JSON.parse(decrypted);
        } catch (error) {
          Sentry.captureException(error);
          results.comparison = data.comparison;
        }
      } else {
        results.comparison = data.comparison;
      }

      setDecryptedData(results);
    };

    decryptAll();
  }, [data, client]);

  useEffect(() => {
    document.querySelector('main')?.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
    >
      <HeaderInfo
        title={
          isFetching ? '' : dayjs(data?.creationDate).format('D MMMM YYYY')
        }
        backUrl={`/${client}/smer`}
      >
        <div className="mt-6 md:mt-8">
          <FadeReplace
            firstElement={
              data ? (
                <div>
                  {(decryptedData.event ||
                    decryptedData.thoughts ||
                    decryptedData.feelInBody ||
                    decryptedData.behaviour) && (
                    <div className="grid gap-4 lg:grid-cols-2">
                      {decryptedData.event && (
                        <InfoCard
                          text={decryptedData.event}
                          title={i18n.t('event')}
                        />
                      )}
                      {decryptedData.thoughts && (
                        <InfoCard
                          text={decryptedData.thoughts}
                          title={i18n.t('thoughts')}
                          startScore={data.beliefLevel}
                          endScore={data.newBeliefLevel}
                        />
                      )}
                      {decryptedData.feelInBody && (
                        <InfoCard
                          text={decryptedData.feelInBody}
                          title={i18n.t('feelInBody')}
                        />
                      )}
                      {decryptedData.behaviour && (
                        <InfoCard
                          text={decryptedData.behaviour}
                          title={i18n.t('behaviour')}
                        />
                      )}
                    </div>
                  )}
                  {Boolean(data.emotions?.length) && (
                    <div className="mt-6 md:mt-8">
                      <div className="text-additional text-sm">
                        {i18n.t('emotions')}
                      </div>
                      <div className="mt-3">
                        {data.emotions.map(emotion => (
                          <EmotionLabel
                            key={emotion.name}
                            emotion={emotion.name}
                            val={emotion.value}
                            isLarge
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {!!data?.errors?.length && (
                    <div className="mt-6 md:mt-8">
                      <div className="text-additional text-sm">
                        {i18n.t('cognitive.distortions')}
                      </div>
                      <div className="mt-3 flex gap-3 overflow-scroll pb-3 md:pb-0">
                        {data.errors?.map(error => (
                          <ErrorItem key={error} title={error} />
                        ))}
                      </div>
                    </div>
                  )}
                  {(decryptedData.solution || decryptedData.conclusion) && (
                    <div className="mt-6 grid gap-4 md:mt-8 lg:grid-cols-2">
                      {decryptedData.solution && (
                        <InfoCard
                          text={decryptedData.solution}
                          title={i18n.t('solution')}
                        />
                      )}
                      {decryptedData.conclusion && (
                        <InfoCard
                          text={decryptedData.conclusion}
                          title={i18n.t('conclusion')}
                        />
                      )}
                    </div>
                  )}
                  {decryptedData.comparison && (
                    <div className="mt-6 md:mt-8">
                      <div className="text-additional text-sm">
                        {i18n.t('arguments')}
                      </div>
                      <div className="mt-3">
                        <Arguments comparison={decryptedData.comparison} />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div />
              )
            }
            secondElement={<SkeletonSmerInner />}
            showFirst={!!data && !isFetching}
          />
        </div>
      </HeaderInfo>
    </motion.div>
  );
};
